/** EMR calculations & text builders. */

export function mergePreVitals(form, preVitals) {
  if (!preVitals || typeof preVitals !== 'object') return form;
  const vitals = { ...(form.vitals || {}) };
  Object.entries(preVitals).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '' && !vitals[k]) vitals[k] = v;
  });
  if (vitals.weight && vitals.height) vitals.bmi = calcBmi(vitals.weight, vitals.height);
  return { ...form, vitals };
}

export function mergeEmrDraft(form, draft) {
  if (!draft || typeof draft !== 'object') return form;
  return {
    ...form,
    ...draft,
    history: draft.history ? { ...(form.history || {}), ...draft.history } : form.history,
    vitals: draft.vitals ? { ...(form.vitals || {}), ...draft.vitals } : form.vitals,
    hpi: draft.hpi ? { ...(form.hpi || {}), ...draft.hpi } : form.hpi,
    assessment: draft.assessment ? { ...(form.assessment || {}), ...draft.assessment } : form.assessment,
    plan: draft.plan ? { ...(form.plan || {}), ...draft.plan } : form.plan,
    examination: draft.examination ? { ...(form.examination || {}), ...draft.examination } : form.examination,
    soap: draft.soap ? { ...(form.soap || {}), ...draft.soap } : form.soap,
    rxLines: Array.isArray(draft.rxLines) ? draft.rxLines.map((r) => ({ ...r })) : form.rxLines,
    labOrders: Array.isArray(draft.labOrders) ? [...draft.labOrders] : form.labOrders,
  };
}

export function normalizePmh(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value).split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
}

export function calcBmi(weightKg, heightCm) {
  const w = parseFloat(weightKg);
  const h = parseFloat(heightCm) / 100;
  if (!w || !h) return '';
  const bmi = w / (h * h);
  return bmi ? bmi.toFixed(1) : '';
}

export function bmiCategory(bmi) {
  const n = parseFloat(bmi);
  if (!n) return '';
  if (n < 18.5) return 'Underweight';
  if (n < 25) return 'Normal';
  if (n < 30) return 'Overweight';
  return 'Obese';
}

export function vitalAbnormal(key, value) {
  if (!value) return false;
  const v = String(value).toLowerCase();
  if (key === 'bp') {
    const m = v.match(/(\d+)\s*\/\s*(\d+)/);
    if (m) return parseInt(m[1], 10) >= 140 || parseInt(m[2], 10) >= 90;
  }
  if (key === 'pulse') {
    const n = parseInt(v, 10);
    return n && (n < 60 || n > 100);
  }
  if (key === 'temp') {
    const n = parseFloat(v);
    return n && (n < 97 || n > 99.5);
  }
  if (key === 'spo2') {
    const n = parseInt(v, 10);
    return n && n < 94;
  }
  return false;
}

// Morning-Afternoon-Night dosing → "1-0-1" (falls back to frequency)
export function dosingLabel(r) {
  const hasMan = [r.m, r.a, r.n].some((x) => x !== '' && x != null);
  if (hasMan) return `${Number(r.m || 0)}-${Number(r.a || 0)}-${Number(r.n || 0)}`;
  return r.frequency || '';
}

// Total units to dispense = (m+a+n) × number of days parsed from duration
export function dispenseQty(r) {
  const perDay = Number(r.m || 0) + Number(r.a || 0) + Number(r.n || 0);
  const days = parseInt(String(r.duration || '').match(/\d+/)?.[0] || '0', 10);
  const qty = perDay * days;
  return qty > 0 ? qty : (perDay > 0 ? perDay : 1);
}

export function buildPrescriptionText(rxLines) {
  return (rxLines || [])
    .filter((r) => r.drug)
    .map((r, i) => {
      const parts = [r.dose, dosingLabel(r), r.food, r.duration, r.route && r.route !== 'Oral' ? r.route : '']
        .filter(Boolean).join(', ');
      const line = `${i + 1}. ${r.drug}${r.generic ? ` (${r.generic})` : ''} — ${parts || '—'}`;
      return r.instructions ? `${line}. ${r.instructions}` : line;
    })
    .join('\n');
}

// Build billable pharmacy lines by matching rx drugs to inventory items.
export function buildPharmacyLines(rxLines, inventory = []) {
  const byId = new Map((inventory || []).map((i) => [i.itemId, i]));
  const byName = (inventory || []).map((i) => ({ i, key: (i.name || '').toLowerCase() }));
  return (rxLines || [])
    .filter((r) => r.drug)
    .map((r) => {
      let inv = r.itemId ? byId.get(r.itemId) : null;
      if (!inv) {
        const q = r.drug.toLowerCase();
        inv = (byName.find((x) => x.key === q) || byName.find((x) => q.includes(x.key) || x.key.includes(q)))?.i || null;
      }
      const qty = dispenseQty(r);
      return {
        itemId: inv?.itemId || '',
        name: inv?.name || r.drug,
        batch: inv?.batch || '',
        mrp: Number(inv?.mrp || 0),
        gstPct: Number(inv?.gstPct || 0),
        discPct: 0,
        qty,
      };
    });
}

// Resolve selected investigation names to structured lab tests using the catalog.
export function buildLabTests(labOrders = [], labCatalog = []) {
  const byName = new Map((labCatalog || []).map((t) => [(t.name || '').toLowerCase(), t]));
  return (labOrders || []).filter(Boolean).map((name) => {
    const t = byName.get(String(name).toLowerCase());
    if (t) {
      const params = Array.isArray(t.parameters) && t.parameters.length
        ? t.parameters
        : [{ name: t.name, unit: t.unit || '', refRange: t.refRange || '', refLow: t.refLow ?? '', refHigh: t.refHigh ?? '' }];
      return { testId: t.testId, name: t.name, rate: Number(t.rate || 0), sampleType: t.sampleType || 'Blood', parameters: params.map((p) => ({ ...p, value: '' })) };
    }
    return { testId: '', name, rate: 0, sampleType: 'Blood', parameters: [{ name, unit: '', refRange: '', refLow: '', refHigh: '', value: '' }] };
  });
}

export function buildLabText(labOrders, labNotes) {
  const tests = (labOrders || []).filter(Boolean);
  const parts = [...tests];
  if (labNotes) parts.push(labNotes);
  return parts.join(', ');
}

export function buildSoapFromForm(form) {
  const hpi = form.hpi || {};
  const subjective = [
    form.complaint && `CC: ${form.complaint}`,
    hpi.onset && `Onset: ${hpi.onset}`,
    hpi.duration && `Duration: ${hpi.duration}`,
    hpi.severity && `Severity: ${hpi.severity}`,
    hpi.associated && `Associated: ${hpi.associated}`,
    form.history?.currentMeds && `Current meds: ${form.history.currentMeds}`,
    form.history?.allergies && `Allergies: ${form.history.allergies}`,
  ].filter(Boolean).join('. ');

  const v = form.vitals || {};
  const objective = [
    v.bp && `BP ${v.bp}`,
    v.pulse && `Pulse ${v.pulse}`,
    v.temp && `Temp ${v.temp}`,
    v.spo2 && `SpO₂ ${v.spo2}`,
    v.weight && `Wt ${v.weight} kg`,
  ].filter(Boolean).join(', ');

  const exam = Object.entries(form.examination || {})
    .filter(([, e]) => e?.finding)
    .map(([id, e]) => `${id.toUpperCase()}: ${e.finding}`)
    .join('; ');

  const assessment = [
    form.assessment?.primary && `Primary: ${form.assessment.primary}`,
    form.assessment?.icd10 && `ICD-10: ${form.assessment.icd10}`,
    form.assessment?.differential && `DDx: ${form.assessment.differential}`,
  ].filter(Boolean).join('. ');

  const plan = [
    buildPrescriptionText(form.rxLines),
    buildLabText(form.labOrders, form.labNotes) && `Investigations: ${buildLabText(form.labOrders, form.labNotes)}`,
    form.plan?.advice && `Advice: ${form.plan.advice}`,
    form.plan?.followUp && `Follow-up: ${form.plan.followUp}`,
  ].filter(Boolean).join('\n');

  return {
    subjective: subjective || form.soap?.subjective || '',
    objective: [objective, exam].filter(Boolean).join('. ') || form.soap?.objective || '',
    assessment: assessment || form.soap?.assessment || '',
    plan: plan || form.soap?.plan || '',
  };
}

/** Quick OPD prescription pad → EMR form shape */
export function rxPadToEmrForm(patient, { diagnoses, meds, vitals, advice, followUp, labTests }) {
  const labOrders = (labTests || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    patientId: patient.patientId || '',
    patientName: patient.patientName || patient.name || '',
    patientAge: patient.age || '',
    patientGender: patient.gender || '',
    visitType: 'OPD',
    complaint: patient.reason || diagnoses[0] || '',
    vitals: {
      bp: vitals.bp || '',
      pulse: vitals.pulse || '',
      temp: vitals.temp || '',
      spo2: vitals.spo2 || '',
      weight: vitals.weight || '',
      rr: vitals.rr || '',
    },
    assessment: { primary: diagnoses.join(', ') },
    rxLines: (meds || [])
      .filter((m) => m.name)
      .map((m) => ({
        drug: m.name,
        dose: m.dose || '',
        frequency: m.freq || '',
        duration: m.dur || '',
        instructions: [m.note, m.route && m.route !== 'Oral' ? m.route : ''].filter(Boolean).join('. '),
        route: m.route || 'Oral',
      })),
    labOrders,
    labNotes: '',
    plan: { advice: advice || '', followUp: followUp || '' },
    notes: advice || '',
  };
}

export function buildEmrPayload(form, user, queueId, recordId, createdAt) {
  const prescription = buildPrescriptionText(form.rxLines);
  const investigations = buildLabText(form.labOrders, form.labNotes);
  const soap = buildSoapFromForm(form);
  return {
    recordId,
    patientId: form.patientId || '',
    patientName: form.patientName,
    patientAge: form.patientAge,
    patientGender: form.patientGender,
    visitType: form.visitType || 'OPD',
    complaint: form.complaint,
    hpi: form.hpi,
    history: form.history,
    vitals: { ...form.vitals, bmi: form.vitals.bmi || calcBmi(form.vitals.weight, form.vitals.height) },
    examination: form.examination,
    assessment: form.assessment,
    diagnosis: form.assessment?.primary || '',
    icd10: form.assessment?.icd10 || '',
    rxLines: (form.rxLines || []).filter((r) => r.drug),
    prescription,
    labOrders: form.labOrders || [],
    labNotes: form.labNotes || '',
    investigations,
    plan: form.plan,
    soap,
    notes: form.notes,
    followUp: form.plan?.followUp || '',
    advice: form.plan?.advice || '',
    referral: form.plan?.referral || '',
    dentalChart: form.dentalChart || undefined,
    privateNotes: form.privateNotes || '',
    healthAssessment: form.healthAssessment || undefined,
    ophthalmology: form.ophthalmology || undefined,
    scribeNote: form.scribeNote || '',
    queueId,
    hospitalId: user.hospitalId,
    doctorName: user.name,
    createdAt: createdAt || new Date().toISOString(),
    status: 'saved',
  };
}
